import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { UserCredentials, User } from '@racepointenergy/swc/scs/types'

import { useAppDispatch } from 'app/hooks'
import { TwoFactorTokenType } from '@racepointenergy/swc/scs/types/user'
import { createUsers2faCheck } from '@racepointenergy/swc/scs/create-users-2fa-check'
import { createUsersLogin } from '@racepointenergy/swc/scs/create-users-login'
import { updateAuthStatusThunk } from 'features/auth/login/slice'
import { storeAuthorizedUser } from 'app/services/LocalStorage'

import {
  SubmitTwoFaWrapper,
  StyledLoginForm,
  StyledResendText,
  StyledButton,
  StyledEmail,
} from './styles'
import { TwoFaFormProps } from '../../types'
import { loginMessages } from '../../messages'

import ResentSuccess from './ResentSuccess'
import ResentFailed from './ResentFailed'
import TwoFaInputs from './TwoFaInputs'
import VerificationFailed from './VerificationFailed'
import CommonButton from 'components/Button/CommonButton'
import { LoaderOverlay } from 'components/Loader'
import LoginCommonLayout from '../LoginCommonLayout'
import { StyledMainHeading } from '../MainHeading/MainHeading'
import { StyledDescriptionText } from '../../styles'
import { LOGIN_ERRORS } from '../../api'

export default function TwoFaForm(props: TwoFaFormProps) {
  const { loginValues, setError } = props
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { formatMessage } = useIntl()
  const [failedError, setFailedError] = useState(false)
  const [code, setCode] = useState('')
  const [resent, setResent] = useState(false)
  const [resentError, setResentError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dispatchCreateUsers2faCheck] = createUsers2faCheck.useMutation()
  const [dispatchCreateUsersLogin] = createUsersLogin.useMutation()

  const handleSubmit = () => {
    const { clientId, userId, twoFactor, password, email } = loginValues
    setLoading(true)
    dispatchCreateUsersLogin({
      email,
      clientId: clientId || '',
      /// @ts-ignore
      userId: userId || '',
      password,
      // TODO SWC fix
      /// @ts-ignore
      twoFactor: [{ type: twoFactor.type as TwoFactorTokenType, code }],
    })
      .unwrap()
      .then((result) => {
        storeAuthorizedUser(result as UserCredentials & User)
        dispatch(updateAuthStatusThunk())
        history.push('/')
        setLoading(false)
      })
      .catch((error) => {
        if (error.data?.error?.code === LOGIN_ERRORS.DOMAIN_ERROR) {
          setError({
            /// @ts-ignore
            code: 179,
            header: formatMessage(loginMessages.domainErrorHeader),
            message: formatMessage(loginMessages.domainError),
          })
          return
        }
        setFailedError(true)
        setCode('')
        setLoading(false)
      })
  }

  const resend = () => {
    setLoading(true)
    const { clientId, email, password } = loginValues
    dispatchCreateUsers2faCheck({
      clientId: clientId || '',
      email,
      password,
      resend: true,
    })
      .unwrap()
      .then((result) => {
        setResent(true)
      })
      .catch((error) => {
        setResentError(true)
      })
      .finally(() => {
        setCode('')
        setLoading(false)
      })
  }

  if (loading) {
    return <LoaderOverlay />
  }

  if (failedError) {
    return (
      <VerificationFailed resend={resend} setFailedError={setFailedError} />
    )
  }

  if (resent) {
    return <ResentSuccess setResent={setResent} />
  }

  if (resentError) {
    return <ResentFailed setResentError={setResentError} resend={resend} />
  }

  return (
    <LoginCommonLayout>
      <StyledMainHeading>
        {formatMessage(loginMessages.twoFaHeading)}
      </StyledMainHeading>
      <StyledDescriptionText>
        {formatMessage(loginMessages.twoFaDescription, {
          email: <StyledEmail>{loginValues.email}</StyledEmail>,
          br: <br />,
        })}
      </StyledDescriptionText>

      <StyledLoginForm onSubmit={handleSubmit}>
        {loading && <LoaderOverlay isFullScreen />}

        <TwoFaInputs onChange={setCode} />

        <SubmitTwoFaWrapper>
          <CommonButton type="button" onClick={() => window.location.reload()}>
            {formatMessage(loginMessages.logoutButton)}
          </CommonButton>
          <CommonButton
            variant="primary"
            type="submit"
            disabled={loading || code.length !== 6}
          >
            {formatMessage(loginMessages.verifyButton)}
          </CommonButton>
        </SubmitTwoFaWrapper>
        <StyledResendText>
          {formatMessage(loginMessages.cantFindEmail)}

          <StyledButton onClick={resend}>
            {formatMessage(loginMessages.resendVerification)}
          </StyledButton>
        </StyledResendText>
      </StyledLoginForm>
    </LoginCommonLayout>
  )
}
